import { saveProfile } from '../dog/Profile';

/**
 * An account is an email, proved by a one-time code, that holds any number of licenses
 * (adoptions). It exists so the same dog comes back on any phone: sign in, pick the license,
 * and the device is set up exactly as the other one. The session token lives in localStorage
 * per device; the license code stays the thing that carers share.
 */
const API = 'https://api.lunaloo.net';
const SESSION_KEY = 'lunaloo.session';

export interface AccountLicense {
  code: string;
  name: string | null;
  dogs: { dog: 'luna' | 'tango'; days: number; togetherSince?: string | null }[];
  members: { device: string; name: string; avatar: string; role: string }[];
  me: { device: string; name: string; avatar: string; role: string } | null;
}

export interface Me {
  email: string;
  licenses: AccountLicense[];
}

export class Account {
  email: string | null = null;
  token: string | null = null;
  /** the last account payload we saw */
  me: Me | null = null;

  constructor(private device: string) {
    try {
      const s = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null') as { email: string; token: string } | null;
      if (s?.token) {
        this.email = s.email;
        this.token = s.token;
      }
    } catch {
      /* no storage */
    }
  }

  get signedIn(): boolean {
    return this.token !== null;
  }

  private async post(path: string, body: unknown): Promise<Response> {
    return fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  private async fail(r: Response): Promise<string> {
    try {
      const j = (await r.json()) as { detail?: string };
      return j.detail ?? `${r.status}`;
    } catch {
      return `${r.status}`;
    }
  }

  /** send a code to the address; the message to show */
  async start(email: string): Promise<{ ok: boolean; message: string }> {
    try {
      const r = await this.post('/v1/auth/start', { email });
      if (!r.ok) return { ok: false, message: await this.fail(r) };
      return { ok: true, message: `code sent to ${email.trim().toLowerCase()} — check your mail` };
    } catch {
      return { ok: false, message: 'needs the internet' };
    }
  }

  /** check the code; on success the device is signed in and `me` is filled */
  async verify(email: string, code: string, license: string | null): Promise<{ ok: boolean; message: string }> {
    try {
      const r = await this.post('/v1/auth/verify', { email, code, device: this.device, license });
      if (!r.ok) return { ok: false, message: await this.fail(r) };
      const j = (await r.json()) as Me & { token: string };
      this.email = j.email;
      this.token = j.token;
      this.me = { email: j.email, licenses: j.licenses };
      localStorage.setItem(SESSION_KEY, JSON.stringify({ email: j.email, token: j.token }));
      return { ok: true, message: 'signed in' };
    } catch {
      return { ok: false, message: 'needs the internet' };
    }
  }

  /** refresh the account; null if signed out (or offline) */
  async refresh(): Promise<Me | null> {
    if (!this.token) return null;
    try {
      const r = await fetch(`${API}/v1/me?token=${encodeURIComponent(this.token)}`);
      if (r.status === 401) {
        this.signOutLocally();
        return null;
      }
      if (!r.ok) return this.me;
      this.me = (await r.json()) as Me;
      return this.me;
    } catch {
      return this.me;
    }
  }

  /** put a license (this device's, usually) on the account */
  async attach(code: string): Promise<boolean> {
    if (!this.token) return false;
    try {
      const r = await this.post('/v1/me/licenses', { token: this.token, code });
      if (!r.ok) return false;
      this.me = (await r.json()) as Me;
      return true;
    } catch {
      return false;
    }
  }

  /** take a license off the account (the license itself lives on for its members) */
  async detach(code: string): Promise<boolean> {
    if (!this.token) return false;
    try {
      const r = await fetch(`${API}/v1/me/licenses/${code}?token=${encodeURIComponent(this.token)}`, { method: 'DELETE' });
      if (!r.ok) return false;
      this.me = (await r.json()) as Me;
      return true;
    } catch {
      return false;
    }
  }

  async signOut(): Promise<void> {
    if (this.token) {
      try {
        await this.post('/v1/auth/signout', { token: this.token });
      } catch {
        /* the token is gone locally either way */
      }
    }
    this.signOutLocally();
  }

  private signOutLocally(): void {
    this.email = null;
    this.token = null;
    this.me = null;
    localStorage.removeItem(SESSION_KEY);
  }

  /** drop this device's license and per-dog caches (before adopting a second dog on the same account) */
  static forgetLicenseLocally(): void {
    for (const k of Object.keys(localStorage)) {
      if (/^lunaloo\.(care|needs|business|food|name|me|code)\b/.test(k)) localStorage.removeItem(k);
    }
  }

  /**
   * Make this device be `license` with `dog`, then reload. Every per-dog cache goes, so the
   * new household's state is pulled clean instead of merged with the old one's.
   */
  static switchTo(license: AccountLicense, dog: 'luna' | 'tango'): void {
    for (const k of Object.keys(localStorage)) {
      if (/^lunaloo\.(care|needs|business|food|name|me)\b/.test(k)) localStorage.removeItem(k);
    }
    localStorage.setItem('lunaloo.code', license.code);
    if (license.name) localStorage.setItem('lunaloo.name', license.name);
    if (license.me) localStorage.setItem('lunaloo.me', JSON.stringify({ name: license.me.name, avatar: license.me.avatar, role: license.me.role }));
    saveProfile(dog);
    location.reload();
  }
}
